import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminAlertText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminLinkAction,
  AdminPanelTitle,
  AdminSectionTitle,
  AdminSupportingText,
  AdminTabButton,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  AdminRowActionsButton,
  AdminRowMenuItem,
  ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
  ADMIN_DETAIL_TABLE_CLASS,
  adminDetailTabContentClassName,
} from '../shared/adminUx';
import { ACTION_POPUP_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';
import {
  display,
  findArchiveCase,
  formatFraudArchiveDateTime,
} from '../../../data/fraudArchiveSpec';

const NOTES_TABLE_GRID =
  'minmax(0, 0.85fr) minmax(0, 2.2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(88px, auto)';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'evidence', label: 'Evidence' },
  { id: 'decision', label: 'Decision' },
  { id: 'notes', label: 'Internal Notes' },
  { id: 'audit', label: 'Audit Log' },
];

const FieldRow = ({ label, children }) => (
  <div className="space-y-1">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    <AdminDetailFieldValue>{children ?? '—'}</AdminDetailFieldValue>
  </div>
);

const FourColumnFields = ({ columns }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
    {columns.map((col, index) => (
      <div key={index} className="space-y-4">
        {col}
      </div>
    ))}
  </div>
);

const splitIntoColumns = (items, columnCount = 4) => {
  const list = items.filter(Boolean);
  const columns = Array.from({ length: columnCount }, () => []);
  const perCol = Math.ceil(list.length / columnCount) || 1;
  list.forEach((item, index) => {
    columns[Math.min(Math.floor(index / perCol), columnCount - 1)].push(item);
  });
  return columns;
};

const Cell = ({ children, center = false }) => (
  <div className={`flex items-center min-w-0 ${center ? 'justify-center' : ''}`}>{children}</div>
);

const SectionBlock = ({ title, children }) => (
  <div className="space-y-4">
    <AdminSectionTitle>{title}</AdminSectionTitle>
    {children}
  </div>
);

const ViewPopup = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
    <div
      className={`bg-white rounded-xl p-6 ${ACTION_POPUP_CONTENT_WIDTH_CLASS} mx-4 shadow-lg border border-lightgray max-h-[90vh] overflow-y-auto`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex justify-between items-center mb-4">
        <AdminPanelTitle className="text-offblack">{title}</AdminPanelTitle>
        <button
          type="button"
          onClick={onClose}
          className="text-offblack hover:text-primaryblue shrink-0"
          aria-label="Close"
        >
          <i className="fi fi-rr-cross text-base" />
        </button>
      </div>
      {children}
    </div>
  </div>
);

const accountPath = (row) => {
  if (!row?.accountId) return null;
  if (row.accountType === 'Customer') return `/admin/users/customers/${row.accountId}`;
  if (row.accountType === 'Business') return `/admin/users/businesses/${row.accountId}`;
  return null;
};

const linkedRecordPath = (item) => {
  if (!item?.recordId) return null;
  if (item.recordType === 'Customer') return `/admin/users/customers/${item.recordId}`;
  if (item.recordType === 'Business') return `/admin/users/businesses/${item.recordId}`;
  return null;
};

const FraudCaseArchiveDetail = () => {
  const { caseId } = useParams();
  const location = useLocation();
  const [caseRow, setCaseRow] = useState(() => findArchiveCase(caseId));
  const [activeTab, setActiveTab] = useState('overview');
  const [viewRequest, setViewRequest] = useState(null);
  const [viewEvidence, setViewEvidence] = useState(null);
  const [viewNote, setViewNote] = useState(null);
  const [openMessage, setOpenMessage] = useState('');
  const [contentEvidenceStaticRowActionsOpen, setContentEvidenceStaticRowActionsOpen] = useState({});
  const contentEvidenceStaticRowActionsRefs = useRef({});

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(contentEvidenceStaticRowActionsRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutsideAll) setContentEvidenceStaticRowActionsOpen({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleContentEvidenceStaticRowActions = (rowKey) => {
    setContentEvidenceStaticRowActionsOpen((prev) => {
      if (prev[rowKey]) return {};
      return { [rowKey]: true };
    });
  };

  useEffect(() => {
    setCaseRow(findArchiveCase(caseId));
  }, [caseId, location.key]);

  const auditRows = useMemo(
    () =>
      [...(caseRow?.auditLog || [])].sort(
        (a, b) => new Date(a.dateTime || 0) - new Date(b.dateTime || 0)
      ),
    [caseRow]
  );

  if (!caseRow) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AdminAlertText className="mb-4">Archived case not found</AdminAlertText>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const decision = caseRow.decision || {};
  const accountHref = accountPath(caseRow);

  const explainUnavailable = (label) => {
    setOpenMessage(`${label} cannot be opened. The retained destination is unavailable.`);
  };

  const renderOverview = () => (
    <FourColumnFields
      columns={splitIntoColumns([
        <FieldRow key="caseId" label="Fraud & Security Case ID">
          {display(caseRow.caseId)}
        </FieldRow>,
        <FieldRow key="decisionId" label="Fraud & Security Decision ID">
          {display(decision.decisionId || caseRow.decisionId)}
        </FieldRow>,
        <FieldRow key="source" label="Source">{display(caseRow.source)}</FieldRow>,
        <FieldRow key="type" label="Type">{display(caseRow.caseType)}</FieldRow>,
        <FieldRow key="caseReason" label="Case Reason">
          {display(caseRow.caseReason)}
        </FieldRow>,
        <FieldRow key="priority" label="Priority">{display(caseRow.priority)}</FieldRow>,
        <FieldRow key="supportTeam" label="Support Team Member">
          {display(caseRow.supportTeamMember || caseRow.assignedTo || decision.decidedBy)}
        </FieldRow>,
        <FieldRow key="received" label="Received">
          {formatFraudArchiveDateTime(caseRow.receivedOn)}
        </FieldRow>,
        <FieldRow key="finalisedOn" label="Finalised On">
          {formatFraudArchiveDateTime(
            caseRow.finalisedOn || decision.finalisedOn || decision.decidedOn
          )}
        </FieldRow>,
        <FieldRow key="account" label="Account">
          {accountHref ? (
            <AdminLinkAction to={accountHref}>{caseRow.accountName}</AdminLinkAction>
          ) : (
            display(caseRow.accountName)
          )}
        </FieldRow>,
        <FieldRow key="decidedBy" label="Decided By">{display(decision.decidedBy)}</FieldRow>,
        <FieldRow key="decidedOn" label="Decided On">
          {formatFraudArchiveDateTime(decision.decidedOn)}
        </FieldRow>,
        ...((caseRow.linkedRecords || []).length
          ? (caseRow.linkedRecords || []).flatMap((item, index) => {
              const href = linkedRecordPath(item);
              return [
                <FieldRow key={`${item.recordId}-${index}-type`} label="Record Type">
                  {display(item.recordType)}
                </FieldRow>,
                <FieldRow key={`${item.recordId}-${index}-id`} label="Record ID">
                  {href ? (
                    <AdminLinkAction to={href}>{item.recordId}</AdminLinkAction>
                  ) : (
                    <AdminLinkAction type="button" onClick={() => explainUnavailable(item.recordId)}>
                      {display(item.recordId)}
                    </AdminLinkAction>
                  )}
                </FieldRow>,
                <FieldRow key={`${item.recordId}-${index}-rel`} label="Relationship">
                  {display(item.relationship)}
                </FieldRow>,
                <FieldRow key={`${item.recordId}-${index}-status`} label="Status">
                  {display(item.status)}
                </FieldRow>,
              ];
            })
          : [<FieldRow key="links-empty" label="Linked Records">—</FieldRow>]),
      ])}
    />
  );

  const renderEvidence = () => (
    <div className="space-y-6">
      <div>
        <AdminSectionTitle className="mb-4">Evidence Details</AdminSectionTitle>
        <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
          <div className="overflow-x-auto">
            <div className="w-full min-w-0">
              <div
                className="grid py-3 text-offblack border-b border-lightgray gap-2 px-4"
                style={{
                  gridTemplateColumns:
                    'minmax(140px, 1.1fr) minmax(260px, 2.6fr) minmax(90px, 0.85fr) minmax(72px, 0.65fr)',
                }}
              >
                <div className="flex items-center min-w-0"><AdminTableHeader>Evidence</AdminTableHeader></div>
                <div className="flex items-center min-w-0"><AdminTableHeader>Details</AdminTableHeader></div>
                <div className="flex items-center min-w-0"><AdminTableHeader>Severity</AdminTableHeader></div>
                <div className="flex items-center justify-center min-w-0"><AdminTableHeader>Actions</AdminTableHeader></div>
              </div>
              {[
                ['Device Match', 'Device ID matched to a prior restricted account', 'High'],
                ['IP Match', 'Same IP used across 3 accounts in 24 hours', 'Medium'],
                ['Contact Behaviour', 'Repeated contact activity across linked accounts', 'Medium'],
              ].map(([evidence, details, severity], idx) => {
                const rowKey = `archive-evidence-${idx}`;
                return (
                  <div
                    key={rowKey}
                    className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-2 items-center"
                    style={{
                      gridTemplateColumns:
                        'minmax(140px, 1.1fr) minmax(260px, 2.6fr) minmax(90px, 0.85fr) minmax(72px, 0.65fr)',
                    }}
                  >
                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate" title={evidence}>{evidence}</AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate" title={details}>{details}</AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0">
                      <AdminTableBody emphasis className="min-w-0 break-words leading-snug">{severity}</AdminTableBody>
                    </div>
                    <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                      <div className="relative inline-flex" ref={(el) => { contentEvidenceStaticRowActionsRefs.current[rowKey] = el; }}>
                        <AdminRowActionsButton
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleContentEvidenceStaticRowActions(rowKey);
                          }}
                          title="Actions"
                          aria-label="Row actions"
                        >
                          <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center" aria-hidden />
                        </AdminRowActionsButton>
                        {contentEvidenceStaticRowActionsOpen[rowKey] && (
                          <div className={ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS}>
                            <AdminRowMenuItem onClick={(e) => { e.stopPropagation(); setContentEvidenceStaticRowActionsOpen({}); }}>
                              View
                            </AdminRowMenuItem>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </DashboardMainContainer>
      </div>
      <div>
        <AdminSectionTitle className="mb-4">Linked Records</AdminSectionTitle>
        <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
          <div className="overflow-x-auto">
            <div className="w-full min-w-0">
              <div
                className="grid py-3 text-offblack border-b border-lightgray gap-2 px-4"
                style={{
                  gridTemplateColumns:
                    'minmax(160px, 1.5fr) minmax(150px, 1.2fr) minmax(120px, 1fr) minmax(72px, 0.65fr)',
                }}
              >
                <div className="flex items-center min-w-0"><AdminTableHeader>Record</AdminTableHeader></div>
                <div className="flex items-center min-w-0"><AdminTableHeader>Type</AdminTableHeader></div>
                <div className="flex items-center min-w-0"><AdminTableHeader>Status</AdminTableHeader></div>
                <div className="flex items-center justify-center min-w-0"><AdminTableHeader>Actions</AdminTableHeader></div>
              </div>
              {[
                ['Ryan Wilson', 'Customer Account', 'Suspended'],
                ['Wilson Home Services', 'Business Account', 'Active'],
                ['MSG-009213', 'Message Record', 'Checked'],
                ['SEC-000441', 'Security Log', 'Recorded'],
              ].map(([record, type, status], idx) => {
                const rowKey = `archive-linked-${idx}`;
                return (
                  <div
                    key={rowKey}
                    className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-2 items-center"
                    style={{
                      gridTemplateColumns:
                        'minmax(160px, 1.5fr) minmax(150px, 1.2fr) minmax(120px, 1fr) minmax(72px, 0.65fr)',
                    }}
                  >
                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate" title={record}>{record}</AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate" title={type}>{type}</AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate" title={status}>{status}</AdminTableBody>
                    </div>
                    <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                      <div className="relative inline-flex" ref={(el) => { contentEvidenceStaticRowActionsRefs.current[rowKey] = el; }}>
                        <AdminRowActionsButton
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleContentEvidenceStaticRowActions(rowKey);
                          }}
                          title="Actions"
                          aria-label="Row actions"
                        >
                          <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center" aria-hidden />
                        </AdminRowActionsButton>
                        {contentEvidenceStaticRowActionsOpen[rowKey] && (
                          <div className={ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS}>
                            <AdminRowMenuItem onClick={(e) => { e.stopPropagation(); setContentEvidenceStaticRowActionsOpen({}); }}>
                              View
                            </AdminRowMenuItem>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </DashboardMainContainer>
      </div>
    </div>
  );

  const renderDecision = () => (
    <div className="space-y-8">
      <FourColumnFields
        columns={splitIntoColumns([
          <FieldRow key="decId" label="Decision ID">{display(decision.decisionId)}</FieldRow>,
          <FieldRow key="severity" label="Confirmed Severity">{display(decision.confirmedSeverity)}</FieldRow>,
          <FieldRow key="final" label="Final Decision">{display(decision.finalDecision)}</FieldRow>,
          <FieldRow key="action" label="Final Account Action">{display(decision.finalAccountAction)}</FieldRow>,
          <FieldRow key="summary" label="Evidence Summary">{display(decision.evidenceSummary)}</FieldRow>,
          <FieldRow key="reason" label="Decision Reason">{display(decision.decisionReason)}</FieldRow>,
          <FieldRow key="by" label="Decided By">{display(decision.decidedBy)}</FieldRow>,
          <FieldRow key="on" label="Decided On">{formatFraudArchiveDateTime(decision.decidedOn)}</FieldRow>,
          <FieldRow key="confirmed" label="Confirmation Time">
            {formatFraudArchiveDateTime(decision.confirmedOn)}
          </FieldRow>,
        ])}
      />

      <SectionBlock title="Information Requests">
        <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
          <div
            className="grid py-3 text-offblack border-b border-lightgray px-4 gap-2"
            style={{
              gridTemplateColumns:
                'minmax(0, 0.9fr) minmax(0, 1fr) minmax(0, 1.4fr) minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 1fr) minmax(62px, auto)',
            }}
          >
            {['Request ID', 'Requested From', 'Request Summary', 'Sent On', 'Due On', 'Status', 'Actions'].map((label) => (
              <div key={label} className="flex items-center min-w-0">
                <AdminTableHeader>{label}</AdminTableHeader>
              </div>
            ))}
          </div>
          {(caseRow.informationRequests || []).length === 0 ? (
            <div className="py-8 text-center">
              <AdminSupportingText className="text-mediumgray">No information requests</AdminSupportingText>
            </div>
          ) : (
            (caseRow.informationRequests || []).map((item) => (
              <div
                key={item.requestId}
                className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-2"
                style={{
                  gridTemplateColumns:
                    'minmax(0, 0.9fr) minmax(0, 1fr) minmax(0, 1.4fr) minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 1fr) minmax(62px, auto)',
                }}
              >
                <Cell><AdminTableBody className="truncate">{item.requestId}</AdminTableBody></Cell>
                <Cell><AdminTableBody className="truncate">{item.requestedFrom}</AdminTableBody></Cell>
                <Cell><AdminTableBody className="truncate">{item.requestSummary}</AdminTableBody></Cell>
                <Cell><AdminTableMeta>{formatFraudArchiveDateTime(item.sentOn)}</AdminTableMeta></Cell>
                <Cell><AdminTableMeta>{formatFraudArchiveDateTime(item.dueOn)}</AdminTableMeta></Cell>
                <Cell><AdminTableBody className="truncate">{item.status}</AdminTableBody></Cell>
                <Cell center>
                  <AdminLinkAction type="button" onClick={() => setViewRequest(item)}>
                    View
                  </AdminLinkAction>
                </Cell>
              </div>
            ))
          )}
        </DashboardMainContainer>
      </SectionBlock>

      <SectionBlock title="Existing Controls">
        <FourColumnFields
          columns={splitIntoColumns([
            <FieldRow key="rStatus" label="Status">{display(caseRow.restriction?.status)}</FieldRow>,
            <FieldRow key="rBy" label="Actor">{display(caseRow.restriction?.appliedBy)}</FieldRow>,
            <FieldRow key="rOn" label="Date and Time">
              {formatFraudArchiveDateTime(caseRow.restriction?.appliedOn)}
            </FieldRow>,
            <FieldRow key="rDuration" label="Duration">{display(caseRow.restriction?.duration)}</FieldRow>,
            <FieldRow key="rEnd" label="End Time">
              {formatFraudArchiveDateTime(caseRow.restriction?.endTime)}
            </FieldRow>,
          ])}
        />
      </SectionBlock>

      <SectionBlock title="Account Protection Record">
        <FourColumnFields
          columns={splitIntoColumns([
            <FieldRow key="pAction" label="Protection Action">
              {display(caseRow.protection?.action)}
            </FieldRow>,
            <FieldRow key="pNote" label="Protection Note">{display(caseRow.protection?.note)}</FieldRow>,
            <FieldRow key="pResult" label="Result">{display(caseRow.protection?.latestResult)}</FieldRow>,
          ])}
        />
      </SectionBlock>

      <SectionBlock title="Finalisation">
        <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
          <div
            className="grid py-3 text-offblack border-b border-lightgray px-4 gap-2"
            style={{
              gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1.6fr) minmax(0, 1fr) minmax(0, 1fr)',
            }}
          >
            {['Step', 'Result', 'Record', 'Completed'].map((label) => (
              <div key={label} className="flex items-center min-w-0">
                <AdminTableHeader>{label}</AdminTableHeader>
              </div>
            ))}
          </div>
          {(caseRow.finalisation || []).length === 0 ? (
            <div className="py-8 text-center">
              <AdminSupportingText className="text-mediumgray">No finalisation records</AdminSupportingText>
            </div>
          ) : (
            (caseRow.finalisation || []).map((step, index) => (
              <div
                key={`${step.step}-${index}`}
                className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-2"
                style={{
                  gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1.6fr) minmax(0, 1fr) minmax(0, 1fr)',
                }}
              >
                <Cell><AdminTableBody className="truncate">{step.step}</AdminTableBody></Cell>
                <Cell><AdminTableBody className="truncate">{display(step.result)}</AdminTableBody></Cell>
                <Cell><AdminTableBody className="truncate">{display(step.record)}</AdminTableBody></Cell>
                <Cell><AdminTableMeta>{formatFraudArchiveDateTime(step.completed)}</AdminTableMeta></Cell>
              </div>
            ))
          )}
        </DashboardMainContainer>
      </SectionBlock>
    </div>
  );

  const renderNotes = () => {
    const sortedNotes = [...(caseRow.internalNotes || [])].sort(
      (a, b) => new Date(b.addedOn || 0) - new Date(a.addedOn || 0)
    );
    return (
      <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
        <div
          className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4"
          style={{ gridTemplateColumns: NOTES_TABLE_GRID }}
        >
          <div className="flex items-center min-w-0">
            <AdminTableHeader>Note ID</AdminTableHeader>
          </div>
          <div className="flex items-center min-w-0">
            <AdminTableHeader>Note</AdminTableHeader>
          </div>
          <div className="flex items-center min-w-0">
            <AdminTableHeader>Added By</AdminTableHeader>
          </div>
          <div className="flex items-center min-w-0">
            <AdminTableHeader>Added On</AdminTableHeader>
          </div>
          <div className="flex items-center justify-center min-w-0">
            <AdminTableHeader>Actions</AdminTableHeader>
          </div>
        </div>
        {sortedNotes.length > 0 ? (
          sortedNotes.map((note) => {
            const noteText = note.text || note.fullText || note.note;
            const noteId = note.id || note.noteId;
            return (
              <div
                key={noteId}
                className="grid py-4 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4"
                style={{ gridTemplateColumns: NOTES_TABLE_GRID }}
              >
                <div className="flex items-start min-w-0">
                  <AdminTableBody className="truncate">{noteId}</AdminTableBody>
                </div>
                <div className="flex items-start min-w-0">
                  <AdminTableBody className="whitespace-pre-wrap break-words">
                    {noteText}
                  </AdminTableBody>
                </div>
                <div className="flex items-start min-w-0">
                  <AdminTableBody emphasis>{note.addedBy}</AdminTableBody>
                </div>
                <div className="flex items-start min-w-0">
                  <AdminTableMeta>{formatFraudArchiveDateTime(note.addedOn)}</AdminTableMeta>
                </div>
                <div className="flex items-start justify-center min-w-0">
                  <AdminLinkAction
                    type="button"
                    onClick={() => setViewNote({ noteId, text: noteText, addedBy: note.addedBy, addedOn: note.addedOn })}
                  >
                    View
                  </AdminLinkAction>
                </div>
              </div>
            );
          })
        ) : (
          <div className="py-8 text-center">
            <AdminSupportingText className="text-mediumgray">No notes yet</AdminSupportingText>
          </div>
        )}
      </DashboardMainContainer>
    );
  };

  const renderAudit = () => (
    <DashboardMainContainer className={`${ADMIN_DETAIL_TABLE_CLASS} overflow-hidden`}>
      <div className="overflow-x-auto">
        <div
          className="grid py-3 text-offblack border-b border-lightgray px-4 gap-2"
          style={{ gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr) minmax(0, 1.8fr) minmax(0, 1.1fr)' }}
        >
          {['Date & Time', 'Event', 'Details', 'Record'].map((label) => (
            <div key={label} className="flex items-center min-w-0">
              <AdminTableHeader>{label}</AdminTableHeader>
            </div>
          ))}
        </div>
        {auditRows.length === 0 ? (
          <div className="py-8 text-center">
            <AdminSupportingText className="text-mediumgray">No audit logs found</AdminSupportingText>
          </div>
        ) : (
          auditRows.map((log, index) => (
            <div
              key={`${log.event}-${log.dateTime}-${index}`}
              className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-2 items-start"
              style={{ gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr) minmax(0, 1.8fr) minmax(0, 1.1fr)' }}
            >
              <Cell><AdminTableMeta className="break-words">{formatFraudArchiveDateTime(log.dateTime)}</AdminTableMeta></Cell>
              <Cell><AdminTableBody className="break-words">{log.event}</AdminTableBody></Cell>
              <Cell><AdminTableBody className="break-words whitespace-pre-wrap">{log.details}</AdminTableBody></Cell>
              <Cell><AdminTableBody className="break-words">{display(log.record)}</AdminTableBody></Cell>
            </div>
          ))
        )}
      </div>
    </DashboardMainContainer>
  );

  return (
    <AdminLayout>
      <div className="tp-container-main">
        <div>
          <DashboardMainContainer className={ADMIN_DETAIL_MAIN_HEADER_CLASS}>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 bg-lightgray flex items-center justify-center">
                <i className="fi fi-rr-shield text-2xl text-offblack"></i>
              </div>
              <div className="flex-1 min-w-0">
                <AdminPanelTitle className="text-offblack">
                  {caseRow.caseId}
                </AdminPanelTitle>
              </div>
            </div>
          </DashboardMainContainer>

          <DashboardMainContainer className="!bg-white">
            <div className="border-b border-lightgray">
              <div className="flex space-x-5 overflow-x-auto">
                {TABS.map((tab) => (
                  <AdminTabButton
                    key={tab.id}
                    active={activeTab === tab.id}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </AdminTabButton>
                ))}
              </div>
            </div>
            <div className={adminDetailTabContentClassName(activeTab)}>
              {openMessage ? <AdminAlertText className="mb-4">{openMessage}</AdminAlertText> : null}
              {activeTab === 'overview' ? renderOverview() : null}
              {activeTab === 'evidence' ? renderEvidence() : null}
              {activeTab === 'decision' ? renderDecision() : null}
              {activeTab === 'notes' ? renderNotes() : null}
              {activeTab === 'audit' ? renderAudit() : null}
            </div>
          </DashboardMainContainer>
        </div>
      </div>

      {viewRequest ? (
        <ViewPopup title="Information Request" onClose={() => setViewRequest(null)}>
          <div className="space-y-4">
            <FieldRow label="Request ID">{viewRequest.requestId}</FieldRow>
            <FieldRow label="Case ID">{display(viewRequest.caseId || caseRow.caseId)}</FieldRow>
            <FieldRow label="Requested From">{viewRequest.requestedFrom}</FieldRow>
            <FieldRow label="Sender">{display(viewRequest.sentBy)}</FieldRow>
            <FieldRow label="Request Summary">{viewRequest.requestSummary}</FieldRow>
            <FieldRow label="Supporting Material">{display(viewRequest.supportingMaterial)}</FieldRow>
            <FieldRow label="Sent On">{formatFraudArchiveDateTime(viewRequest.sentOn)}</FieldRow>
            <FieldRow label="First Available On">{formatFraudArchiveDateTime(viewRequest.firstAvailableOn)}</FieldRow>
            <FieldRow label="Due On">{formatFraudArchiveDateTime(viewRequest.dueOn)}</FieldRow>
            <FieldRow label="Status">{viewRequest.status}</FieldRow>
            <FieldRow label="Response">{display(viewRequest.response)}</FieldRow>
            <FieldRow label="Response Sender">{display(viewRequest.responseSender)}</FieldRow>
            <FieldRow label="Response Submitted On">
              {formatFraudArchiveDateTime(viewRequest.responseSubmittedOn)}
            </FieldRow>
          </div>
        </ViewPopup>
      ) : null}

      {viewEvidence ? (
        <ViewPopup title="Evidence" onClose={() => setViewEvidence(null)}>
          <div className="space-y-4">
            <FieldRow label="Evidence ID">{viewEvidence.evidenceId}</FieldRow>
            <FieldRow label="Type">{viewEvidence.type}</FieldRow>
            <FieldRow label="Evidence">{viewEvidence.evidence}</FieldRow>
            <FieldRow label="Source Record">{display(viewEvidence.sourceRecord)}</FieldRow>
            <FieldRow label="Evidence Date">{display(viewEvidence.evidenceDate)}</FieldRow>
            <FieldRow label="Preserved On">{formatFraudArchiveDateTime(viewEvidence.preservedOn)}</FieldRow>
            <FieldRow label="Status">{viewEvidence.status}</FieldRow>
          </div>
        </ViewPopup>
      ) : null}

      {viewNote ? (
        <ViewPopup title="Internal Note" onClose={() => setViewNote(null)}>
          <div className="space-y-4">
            <FieldRow label="Note ID">{display(viewNote.noteId)}</FieldRow>
            <FieldRow label="Note">{display(viewNote.text)}</FieldRow>
            <FieldRow label="Added By">{display(viewNote.addedBy)}</FieldRow>
            <FieldRow label="Added On">{formatFraudArchiveDateTime(viewNote.addedOn)}</FieldRow>
          </div>
        </ViewPopup>
      ) : null}

    </AdminLayout>
  );
};

export default FraudCaseArchiveDetail;
